import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BOARD_SEARCH_PARAM,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  type PageSizeOption,
} from '../constants/permissions.ts';
import type { BoardListParams } from '../types/board.type.ts';

interface BoardQueryState extends BoardListParams {
  size: PageSizeOption;
}

interface UseBoardQueryStateReturn {
  /** 현재 목록 조회 파라미터 (그대로 useBoardList 에 전달) */
  params: BoardQueryState;
  setPage: (page: number) => void;
  setSize: (size: PageSizeOption) => void;
  setSearch: (search: string) => void;
  setCategory: (category: string | null) => void;
  setSubCategory: (subCategory: string | null) => void;
}

const toInt = (v: string | null, fallback: number) => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : fallback;
};

const normalizeSize = (v: string | null): PageSizeOption => {
  const n = toInt(v, DEFAULT_PAGE_SIZE) as PageSizeOption;
  return PAGE_SIZE_OPTIONS.includes(n) ? n : DEFAULT_PAGE_SIZE;
};

/**
 * 게시판 목록 상태(page/size/search/category/subCategory)를 URL searchParams 와 동기화.
 *
 * 왜 URL 동기화인가:
 *  - 새로고침/뒤로가기/링크 공유에도 같은 목록 상태가 복원된다.
 *  - 상세 → 목록 복귀 시 보던 페이지로 정확히 돌아온다.
 *  - 서버 페이지네이션과 1:1 매핑되어 SSR/크롤러에도 안전.
 *
 * page/size 가 기본값이면 URL 을 더럽히지 않도록 param 을 비운다.
 */
export function useBoardQueryState(): UseBoardQueryStateReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<BoardQueryState>(() => {
    const search = searchParams.get(BOARD_SEARCH_PARAM.SEARCH) ?? '';
    const category = searchParams.get(BOARD_SEARCH_PARAM.CATEGORY);
    const subCategory = searchParams.get(BOARD_SEARCH_PARAM.SUB_CATEGORY);
    return {
      page: toInt(searchParams.get(BOARD_SEARCH_PARAM.PAGE), DEFAULT_PAGE),
      size: normalizeSize(searchParams.get(BOARD_SEARCH_PARAM.SIZE)),
      search: search || undefined,
      category: category || null,
      subCategory: subCategory || null,
    };
  }, [searchParams]);

  /** 부분 패치 헬퍼. 기본값과 같으면 키 제거(클린 URL). */
  const patch = useCallback(
    (next: Partial<Record<string, string | number | null>>) => {
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(next)) {
            if (value === null || value === '' || value === undefined) {
              sp.delete(key);
            } else {
              sp.set(key, String(value));
            }
          }
          return sp;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (page: number) => {
      patch({
        [BOARD_SEARCH_PARAM.PAGE]: page <= DEFAULT_PAGE ? null : page,
      });
    },
    [patch],
  );

  // size/search/category 변경 시 1페이지로 리셋(결과 집합이 바뀌므로)
  const setSize = useCallback(
    (size: PageSizeOption) => {
      patch({
        [BOARD_SEARCH_PARAM.SIZE]: size === DEFAULT_PAGE_SIZE ? null : size,
        [BOARD_SEARCH_PARAM.PAGE]: null,
      });
    },
    [patch],
  );

  const setSearch = useCallback(
    (search: string) => {
      patch({
        [BOARD_SEARCH_PARAM.SEARCH]: search.trim() || null,
        [BOARD_SEARCH_PARAM.PAGE]: null,
      });
    },
    [patch],
  );

  const setCategory = useCallback(
    (category: string | null) => {
      patch({
        [BOARD_SEARCH_PARAM.CATEGORY]: category,
        [BOARD_SEARCH_PARAM.SUB_CATEGORY]: null,
        [BOARD_SEARCH_PARAM.PAGE]: null,
      });
    },
    [patch],
  );

  const setSubCategory = useCallback(
    (subCategory: string | null) => {
      patch({
        [BOARD_SEARCH_PARAM.SUB_CATEGORY]: subCategory,
        [BOARD_SEARCH_PARAM.PAGE]: null,
      });
    },
    [patch],
  );

  return {
    params,
    setPage,
    setSize,
    setSearch,
    setCategory,
    setSubCategory,
  };
}
